/**
 * Sticky Add to Cart for Collection Pages
 * Displays a sticky bar at the bottom when scrolling
 * Allows quick add to cart from collection page
 */

class StickyATCCollection {
  constructor() {
    this.stickyBar = document.querySelector('[data-sticky-atc-collection]');

    if (!this.stickyBar) return;

    this.productInfo = this.stickyBar.querySelector('[data-sticky-product-info]');
    this.productImage = this.stickyBar.querySelector('[data-sticky-product-image]');
    this.productTitle = this.stickyBar.querySelector('[data-sticky-product-title]');
    this.productPrice = this.stickyBar.querySelector('[data-sticky-product-price]');
    this.variantSelect = this.stickyBar.querySelector('[data-sticky-variant-select]');
    this.addToCartBtn = this.stickyBar.querySelector('[data-sticky-add-to-cart]');

    this.currentProduct = null;
    this.scrollThreshold = 300; // Show after scrolling 300px
    this.isVisible = false;

    this.init();
  }

  init() {
    this.setupScrollListener();
    this.setupProductClickListeners();
    this.setupAddToCartListener();
    this.setupVariantChangeListener();
  }

  setupScrollListener() {
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  handleScroll() {
    const scrollY = window.scrollY || window.pageYOffset;

    if (scrollY > this.scrollThreshold && this.currentProduct) {
      this.show();
    } else if (scrollY <= this.scrollThreshold) {
      this.hide();
    }
  }

  show() {
    if (!this.isVisible) {
      this.stickyBar.classList.add('is-visible');
      this.isVisible = true;
    }
  }

  hide() {
    if (this.isVisible) {
      this.stickyBar.classList.remove('is-visible');
      this.isVisible = false;
    }
  }

  setupProductClickListeners() {
    // Listen for clicks on product thumbnails
    const productThumbnails = document.querySelectorAll('.product__thumbnail');

    productThumbnails.forEach(thumbnail => {
      // Add a quick add button or intercept image clicks
      const imageWrapper = thumbnail.querySelector('.product-image__wrapper');

      if (imageWrapper) {
        // Add a quick-add button overlay
        const quickAddBtn = document.createElement('button');
        quickAddBtn.className = 'sticky-atc-quick-btn';
        quickAddBtn.innerHTML = '<span>Quick Add</span>';
        quickAddBtn.setAttribute('aria-label', 'Quick add to sticky cart');

        quickAddBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleProductClick(thumbnail);
        });

        // Add styling for the button
        if (!document.getElementById('sticky-atc-quick-btn-styles')) {
          const style = document.createElement('style');
          style.id = 'sticky-atc-quick-btn-styles';
          style.textContent = `
            .sticky-atc-quick-btn {
              position: absolute;
              bottom: 10px;
              left: 50%;
              transform: translateX(-50%);
              background: rgba(0, 0, 0, 0.8);
              color: white;
              border: none;
              padding: 8px 16px;
              border-radius: 4px;
              font-size: 0.85rem;
              font-weight: 600;
              cursor: pointer;
              opacity: 0;
              transition: opacity 0.2s ease, transform 0.2s ease;
              z-index: 10;
              white-space: nowrap;
            }
            .product__thumbnail:hover .sticky-atc-quick-btn {
              opacity: 1;
            }
            .sticky-atc-quick-btn:hover {
              background: rgba(0, 0, 0, 0.95);
              transform: translateX(-50%) translateY(-2px);
            }
            @media (max-width: 768px) {
              .sticky-atc-quick-btn {
                opacity: 1;
                font-size: 0.75rem;
                padding: 6px 12px;
                bottom: 8px;
              }
            }
          `;
          document.head.appendChild(style);
        }

        // Insert the button if it doesn't exist
        if (!imageWrapper.querySelector('.sticky-atc-quick-btn')) {
          const container = imageWrapper.querySelector('.image__container');
          if (container) {
            container.style.position = 'relative';
            container.appendChild(quickAddBtn);
          }
        }
      }
    });
  }

  handleProductClick(thumbnail) {
    // Extract product data from the thumbnail
    const link = thumbnail.querySelector('a[href*="/products/"]');
    if (!link) return;

    const productUrl = link.getAttribute('href');
    const productId = thumbnail.classList.toString().match(/product-(\d+)/)?.[1];

    if (!productId) return;

    // Fetch product data
    this.fetchProductData(productId, productUrl);
  }

  async fetchProductData(productId, productUrl) {
    try {
      // Get the product JSON
      const response = await fetch(`${productUrl}.js`);
      const productData = await response.json();

      this.currentProduct = productData;
      this.updateStickyBar(productData);

      // Force show the bar
      if (window.scrollY > this.scrollThreshold) {
        this.show();
      } else {
        // Scroll down a bit to trigger the bar
        window.scrollTo({
          top: this.scrollThreshold + 10,
          behavior: 'smooth'
        });
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }

  updateStickyBar(product) {
    // Update product image
    if (product.featured_image) {
      this.productImage.innerHTML = `
        <img src="${this.getImageUrl(product.featured_image, '120x')}"
             alt="${this.escapeHtml(product.title)}"
             loading="lazy">
      `;
    }

    // Update product title
    this.productTitle.textContent = product.title;

    // Update price
    this.updatePrice(product);

    // Update variant selector if multiple variants
    this.updateVariantSelector(product);

    // Enable add to cart button
    this.addToCartBtn.disabled = false;
    this.updateButtonText('Add to Cart');
  }

  updatePrice(product) {
    const selectedVariant = product.variants[0];
    let priceHTML = '';

    if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
      priceHTML = `
        <span class="money sale">${this.formatMoney(selectedVariant.price)}</span>
        <span class="compare-at-price">
          <span class="money">${this.formatMoney(selectedVariant.compare_at_price)}</span>
        </span>
      `;
    } else {
      priceHTML = `<span class="money">${this.formatMoney(selectedVariant.price)}</span>`;
    }

    this.productPrice.innerHTML = priceHTML;
  }

  updateVariantSelector(product) {
    if (product.variants.length > 1) {
      // Create variant selector
      let optionsHTML = product.variants.map(variant => {
        const available = variant.available ? '' : ' (Sold Out)';
        const disabled = !variant.available ? ' disabled' : '';
        return `<option value="${variant.id}"${disabled}>${variant.title}${available}</option>`;
      }).join('');

      this.variantSelect.innerHTML = `
        <select data-variant-selector>
          ${optionsHTML}
        </select>
      `;
      this.variantSelect.style.display = 'block';
    } else {
      this.variantSelect.style.display = 'none';
    }
  }

  setupVariantChangeListener() {
    this.variantSelect.addEventListener('change', (e) => {
      const selector = e.target.closest('select');
      if (selector) {
        const variantId = parseInt(selector.value);
        const variant = this.currentProduct.variants.find(v => v.id === variantId);

        if (variant) {
          this.updatePriceForVariant(variant);
        }
      }
    });
  }

  updatePriceForVariant(variant) {
    let priceHTML = '';

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceHTML = `
        <span class="money sale">${this.formatMoney(variant.price)}</span>
        <span class="compare-at-price">
          <span class="money">${this.formatMoney(variant.compare_at_price)}</span>
        </span>
      `;
    } else {
      priceHTML = `<span class="money">${this.formatMoney(variant.price)}</span>`;
    }

    this.productPrice.innerHTML = priceHTML;
  }

  setupAddToCartListener() {
    this.addToCartBtn.addEventListener('click', () => {
      if (!this.currentProduct) return;

      this.addToCart();
    });
  }

  async addToCart() {
    // Get selected variant
    const variantSelector = this.variantSelect.querySelector('select');
    let variantId;

    if (variantSelector) {
      variantId = parseInt(variantSelector.value);
    } else {
      variantId = this.currentProduct.variants[0].id;
    }

    // Check if variant is available
    const variant = this.currentProduct.variants.find(v => v.id === variantId);
    if (!variant || !variant.available) {
      this.showError('This product is currently unavailable');
      return;
    }

    // Show loading state
    this.addToCartBtn.classList.add('is-loading');
    this.updateButtonText('Adding');

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await response.json();
        this.showSuccess();

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Update cart count if the element exists
        this.updateCartCount();
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Failed to add to cart');
    }
  }

  showSuccess() {
    this.addToCartBtn.classList.remove('is-loading');
    this.addToCartBtn.classList.add('is-success');
    this.updateButtonText('Added!');

    setTimeout(() => {
      this.addToCartBtn.classList.remove('is-success');
      this.updateButtonText('Add to Cart');
    }, 2000);
  }

  showError(message) {
    this.addToCartBtn.classList.remove('is-loading');
    this.updateButtonText('Error');

    setTimeout(() => {
      this.updateButtonText('Add to Cart');
    }, 2000);

    if (console && console.error) {
      console.error(message);
    }
  }

  updateButtonText(text) {
    const buttonText = this.addToCartBtn.querySelector('.sticky-atc-collection__button-text');
    if (buttonText) {
      buttonText.textContent = text;
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // Helper functions
  getImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i, `_${size}.$1$2`);
  }

  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    // Use Shopify's money format if available
    if (typeof Currency !== 'undefined' && Currency.formatMoney) {
      return Currency.formatMoney(cents, Currency.currentCurrency);
    }
    return `$${dollars}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyATCCollection();
  });
} else {
  new StickyATCCollection();
}

// Also initialize on Shopify section events (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new StickyATCCollection();
  });
}
